/**
 * Custom hook for real-time hex updates via Laravel Echo/Reverb.
 * Listens for HexClaimed and HexUpdated events and updates the hex list.
 * Also tracks recently updated hexes for visual highlighting.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type { HexData } from './types';

// How long to highlight recently updated hexes (in milliseconds)
const HIGHLIGHT_DURATION = 5000;

interface HexEventData {
    hex: {
        id: number;
        q: number;
        r: number;
        name: string;
        terrain: string;
        user_id: number;
        username: string | null;
    };
}

export function useRealtimeHexes(initialHexes: HexData[]) {
    const [hexes, setHexes] = useState<HexData[]>(initialHexes);
    // Track recently updated hex IDs for visual highlighting
    const [recentlyUpdatedIds, setRecentlyUpdatedIds] = useState<Set<number>>(new Set());
    // Store timeouts for cleanup
    const timeoutsRef = useRef<Map<number, NodeJS.Timeout>>(new Map());

    // Update hexes when initial data changes (e.g., page navigation)
    useEffect(() => {
        setHexes(initialHexes);
    }, [initialHexes]);

    // Mark a hex as recently updated (for visual highlighting)
    const markAsRecentlyUpdated = useCallback((hexId: number) => {
        // Clear any existing timeout for this hex
        const existingTimeout = timeoutsRef.current.get(hexId);
        if (existingTimeout) {
            clearTimeout(existingTimeout);
        }

        // Add to recently updated set
        setRecentlyUpdatedIds((prev) => new Set(prev).add(hexId));

        // Set timeout to remove the highlight
        const timeout = setTimeout(() => {
            setRecentlyUpdatedIds((prev) => {
                const next = new Set(prev);
                next.delete(hexId);
                return next;
            });
            timeoutsRef.current.delete(hexId);
        }, HIGHLIGHT_DURATION);

        timeoutsRef.current.set(hexId, timeout);
    }, []);

    // Handle a hex being claimed (new hex added)
    const handleHexClaimed = useCallback((data: HexEventData) => {
        setHexes((prev) => {
            // Check if hex already exists (avoid duplicates)
            const exists = prev.some((h) => h.id === data.hex.id);
            if (exists) {
                return prev;
            }
            // Add the new hex
            return [...prev, {
                id: data.hex.id,
                q: data.hex.q,
                r: data.hex.r,
                name: data.hex.name,
                terrain: data.hex.terrain,
                user_id: data.hex.user_id,
                username: data.hex.username,
            }];
        });
        // Mark as recently updated for visual highlighting
        markAsRecentlyUpdated(data.hex.id);
    }, [markAsRecentlyUpdated]);

    // Handle a hex being updated (existing hex modified)
    const handleHexUpdated = useCallback((data: HexEventData) => {
        setHexes((prev) => {
            return prev.map((hex) => {
                if (hex.id === data.hex.id) {
                    return {
                        ...hex,
                        name: data.hex.name,
                        terrain: data.hex.terrain,
                    };
                }
                return hex;
            });
        });
        // Mark as recently updated for visual highlighting
        markAsRecentlyUpdated(data.hex.id);
    }, [markAsRecentlyUpdated]);

    // Subscribe to real-time events
    useEffect(() => {
        // Check if Echo is available
        if (typeof window === 'undefined' || !window.Echo) {
            console.warn('Laravel Echo not available, real-time updates disabled');
            return;
        }

        // Subscribe to the hexmap channel
        const channel = window.Echo.channel('hexmap');

        // Listen for hex claimed events
        channel.listen('HexClaimed', handleHexClaimed);

        // Listen for hex updated events
        channel.listen('HexUpdated', handleHexUpdated);

        // Cleanup on unmount
        return () => {
            channel.stopListening('HexClaimed');
            channel.stopListening('HexUpdated');
            window.Echo.leaveChannel('hexmap');
        };
    }, [handleHexClaimed, handleHexUpdated]);

    // Cleanup timeouts on unmount
    useEffect(() => {
        return () => {
            timeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
            timeoutsRef.current.clear();
        };
    }, []);

    return { hexes, recentlyUpdatedIds };
}

